import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Button } from "./button";
import { ThemeIcon } from "./theme-icon";
import { useTheme } from "../../hooks/useTheme";
import { getAllThemes, getThemeConfig } from "../../utils/theme";
import { type Theme } from "../../store/theme";
import { Check } from "lucide-react";
import { cn } from "../../lib/utils";

interface ThemeToggleProps {
  variant?: "default" | "outline" | "ghost";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
}

export const ThemeToggle = ({ 
  variant = "ghost", 
  size = "icon",
  className 
}: ThemeToggleProps) => {
  const { theme, resolvedTheme, setTheme, systemTheme } = useTheme();

  const handleThemeSelect = (selectedTheme: Theme) => {
    setTheme(selectedTheme);
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button
          variant={variant}
          size={size}
          className={cn(
            "relative transition-colors",
            className
          )}
          aria-label={`Current theme: ${getThemeConfig(theme).label}. Click to change theme.`}
        >
          <ThemeIcon 
            theme={theme} 
            resolvedTheme={resolvedTheme}
            className="h-4 w-4" 
          />
          <span className="sr-only">Toggle theme</span>
        </Button>
      </DropdownMenu.Trigger>
      
      <DropdownMenu.Portal>
        <DropdownMenu.Content 
          className="z-50 min-w-[12rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md animate-in data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
          align="end"
          sideOffset={4}
        >
          <DropdownMenu.Label className="px-2 py-1.5 text-sm font-semibold text-muted-foreground">
            Theme
          </DropdownMenu.Label>
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          
          {getAllThemes().map((themeOption) => {
            const config = getThemeConfig(themeOption);
            const isSelected = theme === themeOption;
            
            return (
              <DropdownMenu.Item
                key={themeOption}
                className="relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                onSelect={() => handleThemeSelect(themeOption)}
              >
                <div className="flex items-center gap-2 flex-1">
                  <ThemeIcon 
                    theme={themeOption}
                    className="h-4 w-4" 
                  />
                  <div className="flex flex-col">
                    <span className="font-medium">{config.label}</span>
                    {themeOption === 'system' && (
                      <span className="text-xs text-muted-foreground">
                        Currently {systemTheme}
                      </span>
                    )}
                  </div>
                </div>
                {isSelected && (
                  <Check className="h-4 w-4 text-primary" />
                )}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}; 